import type { UseSpacesFiltersReturn } from "../hooks/useSpacesFilters";
import type { SpaceWithDisabled } from "../types/assistant";

interface SpacesFilterPanelProps {
  spacesFilters: UseSpacesFiltersReturn;
  onBack: () => void;
  onApply: () => void;
}

interface SpaceCheckboxProps {
  space: SpaceWithDisabled;
  name: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  isRadio?: boolean;
}

function SpaceCheckbox({
  space,
  name,
  checked,
  onChange,
  isRadio = false,
}: SpaceCheckboxProps) {
  const checkbox = (
    <label
      className={`flex items-center gap-2 text-sm cursor-pointer ${
        space.disabled ? "opacity-50 cursor-not-allowed" : ""
      }`}
    >
      <input
        type="checkbox"
        name={name}
        value={space.id}
        checked={checked}
        disabled={space.disabled}
        onChange={(e) => onChange(e.target.checked)}
        className={isRadio ? "rounded-full" : ""}
      />
      <span className="truncate">{space.title}</span>
    </label>
  );

  if (space.disabled) {
    return (
      <div title="You don't have access to this space">
        {checkbox}
      </div>
    );
  }

  return checkbox;
}

export function SpacesFilterPanel({
  spacesFilters,
  onBack,
  onApply,
}: SpacesFilterPanelProps) {
  const {
    filters,
    dispatch,
    teams,
    brandSpaces,
    companySpaces,
    externalReviewersSpaces,
    loading,
  } = spacesFilters;

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-gray-200 dark:border-maven-gray-800 border-t-maven-teal-500 rounded-full animate-spin" />
      </div>
    );
  }

  const handleClearFilters = () => {
    dispatch({ type: "clearAll" });
  };

  return (
    <div className="flex-1 flex flex-col min-h-0">
      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto p-4">
        {/* Source filter */}
        <div className="mb-6">
          <h3 className="font-semibold text-gray-900 mb-3">Sources to include in search</h3>
          <div className="flex flex-row gap-4">
            {(["all", "spaces", "lms"] as const).map((source) => (
              <label
                key={source}
                className="flex items-center gap-2 text-sm cursor-pointer"
              >
                <input
                  type="radio"
                  name="source"
                  value={source}
                  checked={filters.source === source}
                  onChange={() =>
                    dispatch({ type: "setSource", source })
                  }
                />
                <span>
                  {source === "all"
                    ? "All"
                    : source === "spaces"
                      ? "Spaces"
                      : "Training LMS"}
                </span>
              </label>
            ))}
          </div>
        </div>

        {/* Filters section */}
        <h3 className="font-semibold text-gray-900 mb-3">Filters</h3>

        {/* LMS courses role filter — only for LMS or All source */}
        {filters.source !== "spaces" && (
          <div className="mb-6">
            <h3 className="font-medium text-gray-900 mb-3">LMS courses</h3>
            <div className="flex flex-row gap-4">
              {(["APM", "PM", "BM", "SBM"] as const).map((role) => (
                <label
                  key={role}
                  className="flex items-center gap-2 text-sm cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={filters.roles.includes(role)}
                    onChange={() => dispatch({ type: "toggleRole", role })}
                  />
                  <span>{role}</span>
                </label>
              ))}
            </div>
          </div>
        )}

        {filters.source !== "lms" && (
          <>
            <h3 className="font-medium text-gray-900 mb-3">SPACES pages</h3>
            <div className="grid grid-cols-3 gap-6">
              {/* Content type */}
              <div>
                <h3 className="font-medium text-gray-900 mb-3">Content type</h3>
                <div className="space-y-2">
                  {teams.map((team) => (
                    <label
                      key={team.name}
                      className="flex items-center gap-2 text-sm cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={filters.teams.includes(team.name)}
                        onChange={() =>
                          dispatch({
                            type: "toggleTeam",
                            team: team.name,
                          })
                        }
                      />
                      <span>{team.name}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Brands */}
              <div>
                <h3 className="font-medium text-gray-900 mb-3">Brands</h3>
                <div className="grid grid-cols-2 gap-2">
                  {brandSpaces.map((space) => (
                    <SpaceCheckbox
                      key={space.id}
                      space={space}
                      name="brand"
                      checked={filters.brands.includes(space.id.toString())}
                      onChange={(checked) =>
                        dispatch({
                          type: "setBrands",
                          brands: checked
                            ? [...filters.brands, space.id.toString()]
                            : filters.brands.filter(
                                (id) => id !== space.id.toString()
                              ),
                        })
                      }
                    />
                  ))}
                </div>
              </div>

              {/* Company & External Reviewers in one column */}
              <div className="space-y-6">
                {/* Company */}
                <div>
                  <h3 className="font-medium text-gray-900 mb-3">Company</h3>
                  <div className="space-y-2">
                    {companySpaces.map((space) => (
                      <SpaceCheckbox
                        key={space.id}
                        space={space}
                        name="company"
                        isRadio
                        checked={filters.company === space.id.toString()}
                        onChange={(checked) =>
                          dispatch({
                            type: "setCompany",
                            company: checked ? space.id.toString() : null,
                          })
                        }
                      />
                    ))}
                  </div>
                </div>

                {/* External Reviewers */}
                <div>
                  <h3 className="font-medium text-gray-900 mb-3">
                    External Reviewers
                  </h3>
                  <div className="space-y-2">
                    {externalReviewersSpaces.map((space) => (
                      <SpaceCheckbox
                        key={space.id}
                        space={space}
                        name="externalReviewer"
                        isRadio
                        checked={filters.externalReviewer === space.id.toString()}
                        onChange={(checked) =>
                          dispatch({
                            type: "setExternalReviewer",
                            externalReviewer: checked ? space.id.toString() : null,
                          })
                        }
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between gap-3 px-4 py-3 border-t border-gray-200 bg-gray-50">
        <button
          onClick={onBack}
          className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-maven-gray-300 bg-white dark:bg-maven-gray-800 border border-gray-300 dark:border-maven-gray-800 rounded-full hover:bg-gray-50 dark:hover:bg-maven-gray-950"
        >
          Back
        </button>
        <div className="flex items-center gap-3">
          <button
            onClick={handleClearFilters}
            className="px-4 py-2 text-sm font-medium text-white bg-orange-500 rounded-full hover:bg-orange-600"
          >
            Clear Filters
          </button>
          <button
            onClick={onApply}
            className="px-4 py-2 text-sm font-medium text-white bg-maven-teal-500 rounded-full hover:bg-maven-teal-700"
          >
            Apply Filters
          </button>
        </div>
      </div>
    </div>
  );
}
